import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCanAccess } from '@permissions/useFeatures';
import { PencilIcon, TrashIcon } from '@utils/icons';
import { Avatar } from '@components/ui/Avatar/Avatar';
import { DiscardModal } from '@components/ui/DiscardModal/DiscardModal';
import type { ChannelSummary, CommsMember } from '@interfaces/ICommsApi';
import { ConversationInfoDialog } from './ConversationInfoDialog';
import styles from './ChatHeader.module.css';

interface IChatHeaderProps {
  channel: ChannelSummary;
  label: string;
  memberCount: number;
  currentUserId: string;
  members: CommsMember[];
  channels: ChannelSummary[];
  onEdit?: () => void;
  onDelete?: () => void;
  onMessageMember: (userId: string) => void;
  onOpenChannel: (channelId: string) => void;
}

export function ChatHeader({ channel, label, memberCount, currentUserId, members, channels, onEdit, onDelete, onMessageMember, onOpenChannel }: IChatHeaderProps) {
  const { t } = useTranslation();
  // Without `comms.channels` the edit and delete buttons are not rendered.
  const canManageChannels = useCanAccess('comms.channels');
  const [confirmOpen, setConfirmOpen] = useState(false);
  // Clicking the name opens what the conversation is: members, creator, shared channels.
  const [infoOpen, setInfoOpen] = useState(false);
  const isDm = channel.kind === 'dm';
  const isCreator = !isDm && channel.creatorId === currentUserId;

  function handleConfirm() {
    setConfirmOpen(false);
    onDelete?.();
  }

  return (
    <header className={styles.root}>
      <div className={styles.titleRow}>
        <button
          type="button"
          className={styles.identity}
          onClick={() => setInfoOpen(true)}
          aria-label={t('comms.info.open')}
          title={t('comms.info.open')}
        >
          <Avatar name={label} glyph={isDm ? undefined : '#'} size={32} tinted />
          <div className={styles.titleBlock}>
            <h2 className={styles.title}>{label}</h2>
            {!isDm && memberCount > 0 && (
              <span className={styles.meta}>
                {t('comms.header.members', { count: memberCount })}
              </span>
            )}
          </div>
        </button>
        {isCreator && !isDm && canManageChannels && (
          <div className={styles.actions}>
            {onEdit && (
              <button
                type="button"
                className={styles.iconBtn}
                onClick={onEdit}
                aria-label={t('comms.editChannel.tooltip')}
                title={t('comms.editChannel.tooltip')}
              >
                <PencilIcon size={14} />
              </button>
            )}
            {onDelete && (
              <button
                type="button"
                className={`${styles.iconBtn} ${styles.deleteBtn}`}
                onClick={() => setConfirmOpen(true)}
                aria-label={t('comms.deleteChannel.tooltip')}
                title={t('comms.deleteChannel.tooltip')}
              >
                <TrashIcon size={14} />
              </button>
            )}
          </div>
        )}
      </div>

      <ConversationInfoDialog
        open={infoOpen}
        channel={channel}
        label={label}
        currentUserId={currentUserId}
        members={members}
        channels={channels}
        onClose={() => setInfoOpen(false)}
        onMessageMember={onMessageMember}
        onOpenChannel={onOpenChannel}
      />

      <DiscardModal
        open={confirmOpen}
        title={t('comms.deleteChannel.confirm', { name: label })}
        description={t('comms.deleteChannel.description', { name: label })}
        confirmLabel={t('comms.deleteChannel.yes')}
        cancelLabel={t('comms.deleteChannel.no')}
        onConfirm={handleConfirm}
        onCancel={() => setConfirmOpen(false)}
      />
    </header>
  );
}
